/* eslint-disable max-lines */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { find, isEmpty, map, omit } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import LoadingButton from '@mui/lab/LoadingButton';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';

import SelectField from 'common/forms/SelectField';
import TextField from 'common/forms/TextField';
import CheckboxField from 'common/forms/CheckboxField';
import usStateAbbreviation from 'common/constants/usStateAbbreviation';
import { formatZipCode } from 'common/utils';
import { validateCombinedAddressLength, validateZipCode } from 'common/forms/formValidations';
import { updatePatientAddress } from 'store/thunks/patientThunks';
import { patientActions } from 'store/slices/patientSlice';
import RadioGroupField from 'common/forms/RadioGroupField';
import PatientBanner from 'common/modules/PatientBanner';

import useSmsResetWarningDialog from './useSmsResetWarningDialog';

const EditPatientAddressDialog = ({ mpi, patientAddressId, open, onClose }) => {
  const { InfoDialog, showWarning } = useSmsResetWarningDialog();
  const dispatch = useDispatch();
  const patientInfo = useSelector(({ patient }) => patient[mpi]);
  // The address can disappear from the store while the dialog is mounted —
  // the post-mutation refetch may return a backend-normalized list where this
  // row was collapsed into a duplicate — so never dereference it unguarded.
  const patientAddress = find(patientInfo?.addresses, { patientAddressId });
  const patientAddressStatus = patientAddress?.isVerified;
  const notificationPreference = patientInfo?.notificationPreferences;
  const [loading, setLoading] = useState(false);
  const [suggestedAddresses, setSuggestedAddresses] = useState(false);
  const [formAddress, setFormAddress] = useState({});
  const { enqueueSnackbar } = useSnackbar();

  const initialValues = useMemo(
    () => ({
      ...patientAddress?.address,
      nameOnAddress: patientAddress?.nameOnAddress,
      isVerified: patientAddress?.isVerified,
      deliveryInstructions: patientAddress?.deliveryInstructions,
    }),
    [
      patientAddress?.address,
      patientAddress?.isVerified,
      patientAddress?.nameOnAddress,
      patientAddress?.deliveryInstructions,
    ]
  );

  useEffect(() => {
    if (open) {
      setSuggestedAddresses(false);
    }
  }, [open]);

  const handleCloseModal = (_, reason) => {
    if (reason !== 'backdropClick') {
      onClose();
      setFormAddress({});
    }
  };

  const dispatchEditPatientAddress = ({ address }) => {
    setFormAddress(address);
    setLoading(true);
    dispatch(updatePatientAddress({ mpi, address }))
      .then((response) => {
        if (Array.isArray(response) && !isEmpty(response)) {
          setSuggestedAddresses(response);
          return;
        }

        enqueueSnackbar({ variant: 'success', message: 'Patient address updated successfully.' });
        handleCloseModal();
      })
      .catch(() => {
        setSuggestedAddresses([
          {
            line1: address.addressLine1,
            line2: address.addressLine2,
            city: address.city,
            state: address.state,
            zip: address.zip,
          },
        ]);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const handleConfirmAddress = (formData) => {
    const selectedAddress = formData?.isManuallyVerified
      ? suggestedAddresses[0]
      : suggestedAddresses[formData.addressChoice];

    const formattedAddress = {
      nameOnAddress: formAddress?.nameOnAddress,
      patientAddressId: formAddress?.patientAddressId,
      addressLine1: selectedAddress.line1,
      addressLine2: selectedAddress.line2,
      city: selectedAddress.city,
      state: selectedAddress.state,
      zip: selectedAddress.zip,
      isValidationRequired: false,
      isManuallyVerified: formData?.isManuallyVerified,
      deliveryInstructions: formAddress?.deliveryInstructions || undefined,
    };

    setFormAddress(formattedAddress);
    setLoading(true);

    return dispatch(updatePatientAddress({ mpi, address: formattedAddress })).then((success) => {
      if (success) {
        handleCloseModal();
      }
      setLoading(false);
    });
  };

  const resetPhonePreference = () => {
    dispatch(
      patientActions.updatePatientNotificationPreference({
        phone: false,
        email: notificationPreference?.email,
        mpi,
      })
    );
  };

  const handleEditPatientAddress = (formData) => {
    const address = {
      ...omit(formData, 'address', 'createdTimestamp', 'isEditable', 'isVerified'),
      addressLine2: formData.addressLine2 || '',
      patientAddressId,
      isValidationRequired: true,
      deliveryInstructions: formData.deliveryInstructions || undefined,
    };

    const isDisallowedState = !find(usStateAbbreviation, {
      abbreviation: address.state,
    })?.isSMSAllowed;

    showWarning({
      warn: address.isDefault && isDisallowedState && notificationPreference?.phone,
      callback: () => dispatchEditPatientAddress({ address }),
      warnCallback: () => {
        resetPhonePreference();
        dispatch(updatePatientAddress({ mpi, address }));
      },
    });
  };

  return (
    <>
      <InfoDialog />

      <Dialog
        open={open}
        onClose={handleCloseModal}
        aria-describedby='PatientView-EditPatientAddress-header'
        fullWidth
      >
        <DialogTitle id='PatientView-EditAddress-header' sx={{ pb: 0 }}>
          <PatientBanner mpi={mpi} />
        </DialogTitle>

        {!isEmpty(suggestedAddresses) ? (
          <Form
            onSubmit={handleConfirmAddress}
            initialValues={{ addressChoice: '0' }}
            render={({ handleSubmit, invalid }) => (
              <form noValidate onSubmit={handleSubmit}>
                {suggestedAddresses.length > 1 ? (
                  <DialogContent>
                    <Typography variant='h6' sx={{ p: 0, pb: 0 }}>
                      We noticed that the address you entered may not be valid. Please select one
                      from the suggestions below.
                    </Typography>

                    <RadioGroupField
                      name='addressChoice'
                      id='PatientView-NewPatientAddressButton-selectAddress-input'
                      label=''
                    >
                      <Stack direction='column' alignItems='flex-start'>
                        {map(suggestedAddresses, (address, index) => (
                          <FormControlLabel
                            key={`validateAddress-${index}`}
                            value={index}
                            control={<Radio sx={{ mr: 1 }} />}
                            label={
                              <ListItemText
                                primary={
                                  <>
                                    <Typography variant='h6'>{address.line1}</Typography>
                                    {address?.line2 && <Typography>{address.line2}</Typography>}
                                    <Typography>
                                      {address.city} , {address.state}, {address.zip}
                                    </Typography>
                                  </>
                                }
                              />
                            }
                          />
                        ))}
                      </Stack>
                    </RadioGroupField>
                  </DialogContent>
                ) : (
                  <DialogContent>
                    <Typography fontSize='15px' sx={{ p: 0, pb: 0 }}>
                      The Address You Entered Couldn’t Be Verified. You Can Still Choose To Save It
                      As Entered.
                    </Typography>

                    {map(suggestedAddresses, (address, index) => (
                      <CheckboxField
                        required
                        key={index}
                        id='PatientView-NewPatientAddressButton-isManuallyVerified-checkbox'
                        name='isManuallyVerified'
                        label={
                          <Typography fontSize='15px'>{`${address.line1}, ${
                            address.line2 ? `${address.line2}, ` : ''
                          }${address.city}, ${address.state} ${address.zip}`}</Typography>
                        }
                      />
                    ))}
                    <Typography fontSize='15px' sx={{ p: 0, pb: 0 }}>
                      Would You Like To Continue?
                    </Typography>
                  </DialogContent>
                )}
                <DialogActions>
                  <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
                    Cancel
                  </Button>
                  <LoadingButton
                    loading={loading}
                    variant='contained'
                    type='submit'
                    disabled={invalid}
                  >
                    Confirm
                  </LoadingButton>
                </DialogActions>
              </form>
            )}
          />
        ) : (
          <Form
            initialValues={initialValues}
            onSubmit={handleEditPatientAddress}
            render={({ handleSubmit, invalid }) => (
              <form noValidate onSubmit={handleSubmit}>
                <DialogContent>
                  <TextField
                    id='PatientView-EditPatientAddress-nameOnAddress-input'
                    label='Name On Address'
                    name='nameOnAddress'
                    required
                    autoFocus
                  />
                  <TextField
                    id='PatientView-EditPatientAddress-addressLine1-input'
                    label='Address Line 1'
                    name='addressLine1'
                    required
                    validations={[validateCombinedAddressLength()]}
                  />
                  <TextField
                    id='PatientView-EditPatientAddress-addressLine2-input'
                    label='Address Line 2'
                    name='addressLine2'
                    validations={[validateCombinedAddressLength()]}
                  />
                  <TextField
                    id='PatientView-EditPatientAddress-city-input'
                    label='City'
                    name='city'
                    required
                  />

                  <SelectField
                    id='PatientView-EditPatientAddress-state-dropdown'
                    label='State'
                    name='state'
                    required
                  >
                    {map(usStateAbbreviation, ({ name, abbreviation }) => (
                      <MenuItem value={abbreviation} key={abbreviation}>
                        {name}
                      </MenuItem>
                    ))}
                  </SelectField>
                  <TextField
                    id='PatientView-EditPatientAddress-zip-input'
                    label='Zip'
                    name='zip'
                    type='zip'
                    required
                    maxLength={10}
                    format={formatZipCode}
                    validations={[validateZipCode()]}
                  />
                  <TextField
                    id='PatientView-EditPatientAddress-deliveryInstructions-input'
                    label='Delivery Instructions'
                    name='deliveryInstructions'
                    maxLength={200}
                    multiline
                    rows={2}
                  />
                  {patientAddressStatus && (
                    <CheckboxField
                      label='Address Verified'
                      name='isVerified'
                      disabled
                      id='PatientView-EditPatientAddress-verifyAddress-checkBox'
                    />
                  )}
                </DialogContent>

                <DialogActions>
                  <Button variant='outlined' color='secondary' onClick={handleCloseModal}>
                    Cancel
                  </Button>
                  <LoadingButton
                    loading={loading}
                    variant='contained'
                    disabled={invalid}
                    type='submit'
                  >
                    Save
                  </LoadingButton>
                </DialogActions>
              </form>
            )}
          />
        )}
      </Dialog>
    </>
  );
};

EditPatientAddressDialog.propTypes = {
  mpi: PropTypes.string.isRequired,
  patientAddressId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default EditPatientAddressDialog;
